import { useMemo } from "react";

import type { ActivationAlertsContractSeoAlert, ActivationAlertsSeoDetailData } from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";

import { useCommonAlertsQueryParams } from "../useCommonAlertsQueryParams";

import { useSeoAlertsFilterStore } from "./filters/useSeoAlertsFilterStore";
import { getFormattedDuration } from "./getFormattedDuration";

const matchesSearch = (alert: ActivationAlertsContractSeoAlert, searchValue: string): boolean => {
    if (!searchValue) {
        return true;
    }

    const lowerSearch = searchValue.toLowerCase();

    return alert.domain.toLowerCase().includes(lowerSearch) || alert.insight.toLowerCase().includes(lowerSearch);
};

export type SeoAlertItem = ActivationAlertsSeoDetailData[number] & {
    formattedDuration: string;
};

const select = (data: ActivationAlertsSeoDetailData): SeoAlertItem[] =>
    data.map((item) => ({
        ...item,
        formattedDuration: getFormattedDuration(item.weekStartDate),
    }));

export const useSeoAlerts = () => {
    const searchValue = useSeoAlertsFilterStore((s) => s.searchValue);
    const selectedTrafficType = useSeoAlertsFilterStore((s) => s.selectedTrafficType);
    const selectedAlertType = useSeoAlertsFilterStore((s) => s.selectedAlertType);
    const params = useCommonAlertsQueryParams({ primaryCompetitorsOnly: true });

    const { data, ...rest } = useApiQuery(
        {
            tag: "Alerts",
            methodName: "activationAlertsSeoDetail",
            options: {
                select,
                refetchOnMount: true,
            },
        },
        params,
    );

    const filteredData = useMemo(() => {
        return data?.filter((alert) => {
            const isValidTrafficType = !selectedTrafficType || alert.trafficType === selectedTrafficType;
            const isValidAlertType = !selectedAlertType || alert.alertType === selectedAlertType;

            return isValidTrafficType && isValidAlertType && matchesSearch(alert, searchValue);
        });
    }, [data, searchValue, selectedTrafficType, selectedAlertType]);

    return { data: filteredData, ...rest };
};
